import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Search, X } from 'lucide-react';

/* Shared building blocks. Colour carries meaning (status, plan, severity) on clean white cards. */

export type Tone = 'slate' | 'blue' | 'green' | 'amber' | 'red' | 'purple' | 'teal' | 'orange';

const CHIP: Record<Tone, string> = {
  slate: 'bg-slate-100 text-slate-700',
  blue: 'bg-blue-50 text-blue-800',
  green: 'bg-emerald-50 text-emerald-800',
  amber: 'bg-amber-50 text-amber-800',
  red: 'bg-red-50 text-red-700',
  purple: 'bg-violet-50 text-violet-800',
  teal: 'bg-teal-50 text-teal-800',
  orange: 'bg-orange-50 text-orange-800',
};
const ICON_CHIP: Record<Tone, string> = {
  slate: 'bg-slate-100 text-slate-600', blue: 'bg-blue-50 text-blue-700', green: 'bg-emerald-50 text-emerald-700',
  amber: 'bg-amber-50 text-amber-700', red: 'bg-red-50 text-red-600', purple: 'bg-violet-50 text-violet-700',
  teal: 'bg-teal-50 text-teal-700', orange: 'bg-orange-50 text-orange-700',
};
const DOT: Record<Tone, string> = {
  slate: 'bg-slate-400', blue: 'bg-blue-500', green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500',
  purple: 'bg-violet-500', teal: 'bg-teal-500', orange: 'bg-orange-500',
};

export const PLAN_TONE: Record<string, Tone> = { Tropos: 'slate', Strato: 'blue', Meso: 'amber', Thermo: 'orange', Exo: 'purple', Custom: 'teal' };

export function Chip({ tone = 'slate', dot, children, className = '' }: { tone?: Tone; dot?: boolean; children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${CHIP[tone]} ${className}`}>
      {dot && <span className={`h-1.5 w-1.5 rounded-full ${DOT[tone]}`} />}
      {children}
    </span>
  );
}

export function IconChip({ icon: Icon, tone = 'blue', size = 'md' }: { icon: React.ElementType; tone?: Tone; size?: 'sm' | 'md' }) {
  const s = size === 'sm' ? 'h-7 w-7' : 'h-8 w-8';
  return <span className={`inline-flex ${s} shrink-0 items-center justify-center rounded-lg ${ICON_CHIP[tone]}`}><Icon className="h-4 w-4" /></span>;
}

/* Tenant logo, the same way FluxWatch shows vendor logos: the tenant's own logo_url, else the favicon of their
   domain (My Organization, or the admin's work email), else coloured initials. */
const LOGO_COLORS = ['#185FA5', '#0F6E56', '#854F0B', '#993C1D', '#534AB7', '#993556', '#3B6D11', '#5F5E5A'];

export function TenantLogo({ name, domain, logoUrl, size = 32 }: { name: string; domain?: string | null; logoUrl?: string | null; size?: number }) {
  const [failed, setFailed] = useState(0);
  const sources = [logoUrl, domain ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128` : null].filter(Boolean) as string[];
  const src = sources[failed];
  const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const color = LOGO_COLORS[(name || '').split('').reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % LOGO_COLORS.length];
  const box = { width: size, height: size, borderRadius: Math.round(size * 0.25) };
  return src ? (
    <span style={box} className="inline-flex shrink-0 items-center justify-center overflow-hidden border border-slate-200 bg-white">
      <img src={src} alt="" style={{ width: size * 0.7, height: size * 0.7 }} className="object-contain" onError={() => setFailed(f => f + 1)} />
    </span>
  ) : (
    <span style={{ ...box, background: color, fontSize: Math.round(size * 0.38) }} className="inline-flex shrink-0 items-center justify-center font-semibold text-white">
      {initials}
    </span>
  );
}

export function Card({ title, icon, tone = 'blue', right, children, className = '', bodyClass = '' }: {
  title?: React.ReactNode; icon?: React.ElementType; tone?: Tone; right?: React.ReactNode; children: React.ReactNode;
  className?: string; bodyClass?: string;
}) {
  return (
    <section className={`min-w-0 rounded-xl border border-slate-200 bg-white ${className}`}>
      {title && (
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-2.5">
            {icon && <IconChip icon={icon} tone={tone} size="sm" />}
            <h2 className="truncate text-sm font-semibold text-slate-900">{title}</h2>
          </div>
          {right}
        </div>
      )}
      <div className={bodyClass || 'p-5'}>{children}</div>
    </section>
  );
}

type BtnVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
const BTN: Record<BtnVariant, string> = {
  primary: 'bg-blue-700 text-white hover:bg-blue-800 border border-blue-700',
  secondary: 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200',
  danger: 'bg-red-600 text-white hover:bg-red-700 border border-red-600',
  ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 border border-transparent',
};

export function Button({ variant = 'secondary', size = 'md', loading, icon: Icon, children, className = '', ...rest }:
  React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; size?: 'sm' | 'md'; loading?: boolean; icon?: React.ElementType }) {
  const s = size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3.5 py-2 text-sm';
  return (
    <button {...rest} disabled={rest.disabled || loading}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${s} ${BTN[variant]} ${className}`}>
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : Icon ? <Icon className="h-4 w-4" /> : null}
      {children}
    </button>
  );
}

export function Field({ label, hint, error, children }: { label: string; hint?: React.ReactNode; error?: string | null; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-slate-600">{label}</span>
      {children}
      {error ? <span className="mt-1 block text-xs text-red-600">{error}</span>
        : hint ? <span className="mt-1 block text-xs text-slate-500">{hint}</span> : null}
    </label>
  );
}

const INPUT = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100';

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className = '', ...rest }, ref) => <input ref={ref} {...rest} className={`${INPUT} ${className}`} />,
);
export function Select({ className = '', ...rest }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...rest} className={`${INPUT} pr-8 ${className}`} />;
}
export function Textarea({ className = '', ...rest }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} className={`${INPUT} min-h-[84px] ${className}`} />;
}

export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={`${INPUT} w-64 pl-9`} />
    </div>
  );
}

export function Modal({ open, title, onClose, children, footer, width = 'max-w-lg' }: {
  open: boolean; title: React.ReactNode; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode; width?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={onClose}>
      <div className={`w-full ${width} max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-xl`} onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-[65vh] overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

export function Stat({ label, value, sub, icon, tone = 'blue', onClick }: {
  label: string; value: React.ReactNode; sub?: React.ReactNode; icon: React.ElementType; tone?: Tone; onClick?: () => void;
}) {
  const Tag: any = onClick ? 'button' : 'div';
  return (
    <Tag onClick={onClick} className={`min-w-0 rounded-xl border border-slate-200 bg-white p-4 text-left ${onClick ? 'transition hover:-translate-y-px hover:border-blue-300' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium text-slate-500">{label}</span>
        <IconChip icon={icon} tone={tone} size="sm" />
      </div>
      <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{value}</div>
      {sub && <div className="mt-1 truncate text-xs text-slate-500">{sub}</div>}
    </Tag>
  );
}

export function Bar({ pct, tone }: { pct: number | null; tone?: Tone }) {
  const p = Math.max(0, Math.min(100, pct ?? 0));
  const t: Tone = tone || (p >= 100 ? 'red' : p >= 80 ? 'amber' : 'blue');
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${DOT[t]}`} style={{ width: `${p}%`, minWidth: p > 0 ? 4 : 0 }} />
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: React.ReactNode; subtitle?: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Tabs<T extends string>({ value, onChange, tabs }: { value: T; onChange: (v: T) => void; tabs: { key: T; label: string; count?: number }[] }) {
  return (
    <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
      {tabs.map(t => (
        <button key={t.key} onClick={() => onChange(t.key)}
          className={`-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
            value === t.key ? 'border-blue-700 text-blue-800' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
          {t.label}
          {t.count != null && <span className={`rounded-full px-1.5 text-xs ${value === t.key ? 'bg-blue-50 text-blue-800' : 'bg-slate-100 text-slate-600'}`}>{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { key: T; label: string; count?: number }[] }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
      {options.map(o => (
        <button key={o.key} onClick={() => onChange(o.key)}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${value === o.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
          {o.label}{o.count != null && <span className="ml-1 opacity-70">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Empty({ icon: Icon, title, text, action }: { icon: React.ElementType; title: string; text?: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
      <span className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500"><Icon className="h-5 w-5" /></span>
      <div className="text-sm font-medium text-slate-800">{title}</div>
      {text && <div className="mt-1 max-w-sm text-sm text-slate-500">{text}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorBanner({ text, onRetry }: { text: string; onRetry?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />{text}</span>
      {onRetry && <Button size="sm" onClick={onRetry}>Try again</Button>}
    </div>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-lg bg-slate-200/70 ${className}`} />;
}

export function PageSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-60" />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-28" />)}</div>
      <Skeleton className="h-72" />
    </div>
  );
}

/* ─── toasts ─────────────────────────────────────────────────────────────── */

type ToastItem = { id: number; kind: 'ok' | 'error'; text: string };
const ToastCtx = createContext<(kind: 'ok' | 'error', text: string) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((kind: 'ok' | 'error', text: string) => {
    const id = Date.now() + Math.random();
    setItems(list => [...list, { id, kind, text }]);
    window.setTimeout(() => setItems(list => list.filter(t => t.id !== id)), kind === 'ok' ? 3500 : 6000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="fixed bottom-5 right-5 z-[60] flex w-80 flex-col gap-2">
        {items.map(t => (
          <div key={t.id} className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm shadow-lg ${
            t.kind === 'ok' ? 'border-emerald-200 bg-white text-slate-800' : 'border-red-200 bg-white text-red-700'}`}>
            {t.kind === 'ok' ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
